import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security, LoginCallback } from "@okta/okta-react";
import { Provider } from "react-redux";
import { SnackbarProvider } from "notistack";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { DashboardLayout, PageContainer } from "@toolpad/core";
import type { Navigation, Branding } from "@toolpad/core";
import HomeIcon from "@mui/icons-material/Home";
import ExtensionIcon from "@mui/icons-material/Extension";
import PeopleIcon from "@mui/icons-material/People";

import { theme } from "./theme/theme";
import { getOktaAuth, LOGIN_CALLBACK_PATH } from "./services/oktaAuth";
import { store } from "./store";
import { RequiredAuth } from "./components/RequiredAuth";
import { UserAccount } from "./components/UserAccount";
import { HomePage } from "./pages/HomePage";
import { ExtensionPage } from "./pages/ExtensionPage";
import { AccountsPage } from "./pages/AccountsPage";

const NAVIGATION: Navigation = [
  { title: "Home", icon: <HomeIcon />, segment: "" },
  { title: "Accounts", icon: <PeopleIcon />, segment: "accounts" },
  { title: "Extension", icon: <ExtensionIcon />, segment: "extension" },
];

const BRANDING: Branding = {
  title: "SQLAPI Support",
};

function RootLayout() {
  const navigate = useNavigate();
  const restoreOriginalUri = async (
    _oktaAuth: OktaAuth,
    originalUri: string,
  ) => {
    navigate(toRelativeUrl(originalUri, window.location.origin), {
      replace: true,
    });
  };

  return (
    <ReactRouterAppProvider
      navigation={NAVIGATION}
      branding={BRANDING}
      theme={theme}
    >
      <Security
        oktaAuth={getOktaAuth()}
        restoreOriginalUri={restoreOriginalUri}
      >
        <Outlet />
      </Security>
    </ReactRouterAppProvider>
  );
}

function AppLayout() {
  return (
    <DashboardLayout slots={{ toolbarActions: UserAccount }}>
      <PageContainer>
        <Outlet />
      </PageContainer>
    </DashboardLayout>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            element: <RequiredAuth />,
            children: [
              { index: true, element: <HomePage /> },
              { path: "accounts", element: <AccountsPage /> },
              { path: "extension", element: <ExtensionPage /> },
            ],
          },
        ],
      },
      { path: LOGIN_CALLBACK_PATH, element: <LoginCallback /> },
    ],
  },
]);

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
        >
          <RouterProvider router={router} />
        </SnackbarProvider>
      </ThemeProvider>
    </Provider>
  );
}

export { App };
